import {
  persistSessionIdFromResponse,
  withSessionHeader,
} from "../../tools/helper";
import { format, parse } from "date-fns";

import React, { useEffect } from "react";
import { ReactNode, RefObject } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import { DatePicker } from "@mui/x-date-pickers";
import { useCsConfiguration } from "@plansoft-configuration-renderer/framework";

export interface ResultsTableRow {
  id: string;
  createdate: string;
  description: string;
  creator: string;
  update_user: string;
  shop_id: string;
  version: string;
  errorStateEmpty: string;
  errorBlocked: string;
}

type LoadConfigurationFormContentProps = {
  submitFormRef: RefObject<HTMLFormElement | null>;
  onShowResults: (
    data: ResultsTableRow[],
    formData: Record<string, unknown>,
  ) => void;
  formData: Record<string, unknown> | null;
  setFormData: (data: Record<string, unknown>) => void;
};

export default function LoadConfigurationFormContent({
  submitFormRef,
  onShowResults,
  formData,
  setFormData,
}: LoadConfigurationFormContentProps): ReactNode {
  const { t } = useTranslation();
  const { localeId } = useCsConfiguration();
  const { register, handleSubmit, setValue, reset, watch } = useForm({
    defaultValues: formData || {},
  });

  const dateFormatMap: Record<string, string> = {
    "en-US": "MM/dd/yyyy",
    "de-DE": "dd.MM.yyyy",
    "pl-PL": "dd.MM.yyyy",
    "fr-FR": "dd/MM/yyyy",
    "it-IT": "dd/MM/yyyy",
    "nl-NL": "dd-MM-yyyy",
    "cz-CZ": "dd.MM.yyyy",
    "dk-DK": "dd-MM-yyyy",
    "hu-HU": "yyyy.MM.dd.",
    default: "dd.MM.yyyy",
  };
  const dateFormat =
    dateFormatMap[localeId ?? "default"] || dateFormatMap.default;

  const parseStoredDate = (dateString: string): Date | null => {
    try {
      const parsedDate = parse(dateString, dateFormat, new Date());
      return isNaN(parsedDate.getTime()) ? null : parsedDate;
    } catch {
      return null;
    }
  };

  useEffect(() => {
    if (formData) {
      reset(formData);
    }
  }, [formData, reset]);

  const formFields = [
    {
      label: t("load_configuration_dialog.guid"),
      name: "GUID",
    },
    {
      label: t("load_configuration_dialog.material_number"),
      name: "MaterialNumber",
    },
    {
      label: t("load_configuration_dialog.order_code"),
      name: "OrderCode",
    },
    {
      label: t("load_configuration_dialog.creator"),
      name: "Creator",
    },
  ];

  const datePickers = [
    { label: t("load_configuration_dialog.start_date"), name: "From" },
    { label: t("load_configuration_dialog.end_date"), name: "To" },
  ];

  const onSubmit = async (data: Record<string, unknown>): Promise<void> => {
    try {
      const response = await fetch("/api/v1/pg/filter", {
        method: "POST",
        headers: withSessionHeader({
          accept: "*/*",
          "Content-Type": "application/json; X-Version=1.0",
        }),
        body: JSON.stringify(data),
      });

      persistSessionIdFromResponse(response);

      const responseData = await response.text();

      try {
        const parsedData = JSON.parse(responseData);
        if (Array.isArray(parsedData)) {
          setFormData(data);
          onShowResults(parsedData, data);
        }
      } catch {
        // TODO: [KS] handle error, if needed
      }
    } catch (error) {
      console.error("API call failed:", error);
    }
  };

  return (
    <form ref={submitFormRef} onSubmit={handleSubmit(onSubmit)}>
      <Box
        sx={{
          display: "flex",
          margin: "0 auto",
          justifyContent: "space-between",
          gap: 2,
          flexWrap: "wrap",
        }}
      >
        {formFields.map(({ label, name }, index) => (
          <Box key={index} sx={{ minWidth: "45%" }}>
            <Box>{label}</Box>
            <TextField
              sx={{ width: "100%" }}
              {...register(`${name}`, { required: false })}
            />
          </Box>
        ))}

        {datePickers.map(({ name, label }, index) => (
          <Box key={index} sx={{ minWidth: "45%" }}>
            <Box>{label}</Box>
            <DatePicker
              slotProps={{ textField: { size: "small" } }}
              sx={{
                width: "100%",
                marginTop: "0.5rem",
                "& .MuiInputLabel-root": { display: "none" },
                "& legend": { display: "none" },
              }}
              value={
                // eslint-disable-next-line react-hooks/incompatible-library -- react-hook-form's watch() is inherently unmemoizable
                watch(name) ? parseStoredDate(watch(name) as string) : null
              }
              onChange={(date) => {
                if (date) {
                  setValue(name, format(date, dateFormat));
                } else {
                  setValue(name, null);
                }
              }}
              label={label}
              format={dateFormat}
            />
          </Box>
        ))}
      </Box>
    </form>
  );
}
